// TEN18 (docs/build-briefs/TEN18-publish-discounts-adopt-by-receipt.md; ADR-008, DEC-342 Q6, P0.21): the location's
// "From <network name>" area in Settings > Discounts. Lists what the franchise network offers this location with Accept
// and Decline (TEN18-F1). Wiring only: every decision is in networkDiscountOffersModel.ts, and the API calls are handed in by
// admin.ts so this file holds no session logic. All text goes in with textContent.

import {
  adoptedMessage,
  declinedMessage,
  describeOfferFailure,
  isOffersShape,
  OFFERS_LOAD_FAILED,
  offerViews,
  offersHeading,
  showsOffers,
  type OfferView,
  type OffersResponseDto,
} from "./networkDiscountOffersModel.ts";

interface Reply<T> {
  status: number;
  body: T;
}

export interface NetworkOffersDeps {
  section: HTMLElement;
  title: HTMLElement;
  list: HTMLElement;
  status: HTMLElement;
  /** GET the offers. */
  fetchOffers: () => Promise<Reply<unknown>>;
  /** POST adopt or decline for one publication. */
  answer: (publicationId: string, kind: "adopt" | "decline") => Promise<Reply<{ error?: string; message?: string } | null>>;
  /** Called after a successful adopt, so the discount list below re-reads and shows the new copy. */
  onAdopted: () => void;
  onAnswered?: () => void;
  toast: (message: string, variant: "success") => void;
  /** False once the session or screen the request began under is gone; a late reply then does nothing. */
  isCurrent: () => boolean;
}

export interface NetworkOffersControl {
  /** Loads and draws the area; hides it when the location has nothing offered. */
  refresh: () => Promise<void>;
  /** Hides and empties the area (sign-out, tenant switch). */
  reset: () => void;
}

export function createNetworkOffers(deps: NetworkOffersDeps): NetworkOffersControl {
  let response: OffersResponseDto | null = null;
  let busy = false;

  function reset(): void {
    response = null;
    deps.section.hidden = true;
    deps.list.replaceChildren();
    deps.status.textContent = "";
  }

  function renderOffer(view: OfferView): HTMLElement {
    const item = document.createElement("li");
    item.className = "admin-network-offer";
    item.setAttribute("data-testid", "admin-network-offer");
    item.setAttribute("data-publication-name", view.name);

    const text = document.createElement("div");
    text.className = "admin-network-offer-text";
    const name = document.createElement("span");
    name.className = "admin-network-offer-name";
    name.setAttribute("data-testid", "admin-network-offer-name");
    name.textContent = view.name;
    const terms = document.createElement("span");
    terms.className = "admin-network-offer-terms";
    terms.setAttribute("data-testid", "admin-network-offer-terms");
    terms.textContent = view.termsText;
    const locations = document.createElement("span");
    locations.className = "admin-network-offer-terms";
    locations.setAttribute("data-testid", "admin-network-offer-locations");
    locations.textContent = view.locationsText;
    const windowEl = document.createElement("span");
    windowEl.className = "admin-network-offer-terms";
    windowEl.setAttribute("data-testid", "admin-network-offer-window");
    windowEl.textContent = view.windowText;
    const stacking = document.createElement("span");
    stacking.className = "admin-network-offer-terms";
    stacking.setAttribute("data-testid", "admin-network-offer-stacking");
    stacking.textContent = view.stackingText;
    text.append(name, terms, locations, windowEl, stacking);

    const state = document.createElement("span");
    state.className = `so-badge so-badge-${view.tone} admin-network-offer-state`;
    state.setAttribute("data-testid", "admin-network-offer-state");
    const dot = document.createElement("span");
    dot.className = "so-badge-dot";
    dot.setAttribute("aria-hidden", "true");
    state.append(dot, view.stateLabel);

    const actions = document.createElement("div");
    actions.className = "admin-network-offer-actions";
    if (view.canAccept) {
      const accept = document.createElement("button");
      accept.type = "button";
      accept.className = "btn-primary btn-compact";
      accept.setAttribute("data-testid", "admin-network-offer-accept");
      accept.textContent = "Accept";
      accept.setAttribute("aria-label", `Accept ${view.name}`);
      accept.addEventListener("click", () => void answer(view, "adopt"));
      actions.append(accept);
    }
    if (view.canDecline) {
      const decline = document.createElement("button");
      decline.type = "button";
      decline.className = "btn-secondary btn-compact";
      decline.setAttribute("data-testid", "admin-network-offer-decline");
      decline.textContent = "Decline";
      decline.setAttribute("aria-label", `Decline ${view.name}`);
      decline.addEventListener("click", () => void answer(view, "decline"));
      actions.append(decline);
    }
    item.append(text, state, actions);
    return item;
  }

  function render(): void {
    if (!showsOffers(response) || response === null || response.network === null) {
      reset();
      return;
    }
    deps.title.textContent = offersHeading(response.network.name);
    deps.list.replaceChildren(...offerViews(response.offers).map(renderOffer));
    deps.section.hidden = false;
  }

  async function answer(view: OfferView, kind: "adopt" | "decline"): Promise<void> {
    if (busy) return;
    busy = true;
    for (const button of deps.list.querySelectorAll("button")) button.disabled = true;
    deps.status.textContent = "";
    const result = await deps.answer(view.publicationId, kind);
    busy = false;
    if (!deps.isCurrent()) return;
    if (result.status === 200 || result.status === 201) {
      deps.toast(kind === "adopt" ? adoptedMessage(view.name) : declinedMessage(view.name), "success");
      deps.onAnswered?.();
      await refresh();
      if (kind === "adopt") deps.onAdopted();
      return;
    }
    deps.status.textContent = describeOfferFailure(result.status, result.body);
    for (const button of deps.list.querySelectorAll("button")) button.disabled = false;
  }

  async function refresh(): Promise<void> {
    const result = await deps.fetchOffers();
    if (!deps.isCurrent()) return;
    if (result.status !== 200 || !isOffersShape(result.body)) {
      // A location with no network answers 200 with no network; anything else is a failure worth one sentence.
      if (result.status === 403) {
        reset();
        return;
      }
      deps.title.textContent = "From your franchise network";
        deps.list.replaceChildren();
      deps.status.textContent = OFFERS_LOAD_FAILED;
      deps.section.hidden = false;
      return;
    }
    deps.status.textContent = "";
    response = result.body;
    render();
  }

  return { refresh, reset };
}
